import * as React from "react";
import { Card, Icon, Message, Segment } from "semantic-ui-react";

type UploadCardProps = {
  isUploading: boolean;
  uploadError: string | null;
  onUpload: (jsonContent: string) => void;
};

type UploadCardState = {
  isDragOver: boolean;
  validationError: string | null;
  fileName: string | null;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

class UploadCard extends React.Component<UploadCardProps, UploadCardState> {
  private fileInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: UploadCardProps) {
    super(props);
    this.state = {
      isDragOver: false,
      validationError: null,
      fileName: null,
    };
    this.fileInputRef = React.createRef();
  }

  private handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: true });
  };

  private handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: false });
  };

  private handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: false });

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      this.processFile(files[0]);
    }
  };

  private handleBrowseClick = (): void => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  private handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  };

  private processFile = (file: File): void => {
    // Validate extension
    if (!file.name.toLowerCase().endsWith(".json")) {
      this.setState({ validationError: "Only .json files are accepted", fileName: null });
      return;
    }

    // Validate size
    if (file.size > MAX_FILE_SIZE) {
      this.setState({ validationError: "File size exceeds 10 MB limit", fileName: null });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;

      // Validate JSON parse
      try {
        JSON.parse(content);
      } catch {
        this.setState({ validationError: "File is not valid JSON", fileName: null });
        return;
      }

      this.setState({ validationError: null, fileName: file.name });
      this.props.onUpload(content);
    };
    reader.onerror = () => {
      this.setState({ validationError: "Failed to read file", fileName: null });
    };
    reader.readAsText(file);
  };

  render(): JSX.Element {
    const { isUploading, uploadError } = this.props;
    const { isDragOver, validationError, fileName } = this.state;

    return (
      <Card fluid>
        <Card.Content>
          <Card.Header>Upload JSON</Card.Header>
          <Card.Description>
            <Segment
              placeholder
              textAlign="center"
              onDragOver={this.handleDragOver}
              onDragLeave={this.handleDragLeave}
              onDrop={this.handleDrop}
              style={{
                border: isDragOver ? "2px dashed #2185d0" : "2px dashed #ccc",
                backgroundColor: isDragOver ? "#f0f8ff" : undefined,
                cursor: "pointer",
              }}
              onClick={this.handleBrowseClick}
            >
              <Icon name="cloud upload" size="big" color={isDragOver ? "blue" : "grey"} />
              <p style={{ marginTop: "0.5em" }}>
                Drag & drop a .json file here or click to browse
              </p>
              {fileName && (
                <p>
                  <Icon name="file" /> {fileName}
                </p>
              )}
            </Segment>
            <input
              ref={this.fileInputRef}
              type="file"
              accept=".json"
              style={{ display: "none" }}
              onChange={this.handleFileChange}
            />
            {isUploading && (
              <Message info icon>
                <Icon name="spinner" loading />
                <Message.Content>Uploading...</Message.Content>
              </Message>
            )}
            {validationError && (
              <Message negative>
                <Message.Header>Validation Error</Message.Header>
                <p>{validationError}</p>
              </Message>
            )}
            {uploadError && (
              <Message negative>
                <Message.Header>Upload Error</Message.Header>
                <p>{uploadError}</p>
              </Message>
            )}
          </Card.Description>
        </Card.Content>
      </Card>
    );
  }
}

export default UploadCard;
